import { useEffect, useState } from 'react'
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Container,
  FileInput,
  Group,
  Loader,
  Modal,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { Edit2, Plus, Trash2 } from 'lucide-react'
import {
  learningMaterialsApi,
  type LearningMaterialItem,
  type MasteryOption,
} from '../../api/learningMaterials'

const MEDIA_TYPES = [
  { value: 'document', label: 'Document' },
  { value: 'text', label: 'Text' },
  { value: 'video', label: 'Video' },
  { value: 'audio', label: 'Audio' },
  { value: 'link', label: 'Link' },
]

const MEDIA_COLORS: Record<string, string> = {
  document: 'red',
  text: 'blue',
  video: 'grape',
  audio: 'orange',
  link: 'teal',
}

export function ContentMgr() {
  const [items, setItems] = useState<LearningMaterialItem[]>([])
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)

  const [filterType, setFilterType] = useState<string | null>(null)
  const [filterMastery, setFilterMastery] = useState<string | null>(null)
  const [masteries, setMasteries] = useState<MasteryOption[]>([])

  const [modalOpen, setModalOpen] = useState(false)
  const [editId, setEditId] = useState<number | null>(null)
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [mediaType, setMediaType] = useState<string | null>(null)
  const [mediaUrl, setMediaUrl] = useState('')
  const [masteryId, setMasteryId] = useState<string | null>(null)
  const [uploadFile, setUploadFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)
  const [submitting, setSubmitting] = useState(false)

  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null)
  const [deleting, setDeleting] = useState(false)

  useEffect(() => {
    learningMaterialsApi.getAllMasteries().then((r) => setMasteries(r.data)).catch(() => {})
  }, [])

  useEffect(() => {
    fetchItems(page)
  }, [page, filterType, filterMastery])

  function fetchItems(p: number) {
    setLoading(true)
    learningMaterialsApi
      .list({
        page: p,
        per_page: 15,
        media_type: filterType || undefined,
        mastery_id: filterMastery ? Number(filterMastery) : undefined,
      })
      .then((res) => {
        setItems(res.data.items)
        setTotal(res.data.total)
        setTotalPages(res.data.total_pages)
      })
      .catch(() => notifications.show({ title: 'Error', message: 'Failed to load content', color: 'red' }))
      .finally(() => setLoading(false))
  }

  function openCreate() {
    setEditId(null)
    setTitle('')
    setContent('')
    setMediaType(null)
    setMediaUrl('')
    setMasteryId(null)
    setUploadFile(null)
    setModalOpen(true)
  }

  function openEdit(item: LearningMaterialItem) {
    setEditId(item.id)
    setTitle(item.title)
    setContent(item.content || '')
    setMediaType(item.media_type)
    setMediaUrl(item.media_url || '')
    setMasteryId(item.mastery_id ? String(item.mastery_id) : null)
    setUploadFile(null)
    setModalOpen(true)
  }

  async function handleUpload(): Promise<string | null> {
    if (!uploadFile) return null
    setUploading(true)
    try {
      const res = await learningMaterialsApi.upload(uploadFile)
      return res.data.url
    } catch {
      notifications.show({ title: 'Error', message: 'Upload failed', color: 'red' })
      return null
    } finally {
      setUploading(false)
    }
  }

  async function handleSubmit() {
    if (!title.trim()) {
      notifications.show({ title: 'Validation', message: 'Title required', color: 'orange' })
      return
    }

    setSubmitting(true)
    try {
      let finalUrl = mediaUrl

      if (uploadFile && mediaType === 'document') {
        const uploaded = await handleUpload()
        if (!uploaded) { setSubmitting(false); return }
        finalUrl = uploaded
      }

      const payload = {
        title: title.trim(),
        mastery_id: masteryId ? Number(masteryId) : null,
        content: content || null,
        media_type: mediaType,
        media_url: finalUrl || null,
      }

      if (editId) {
        await learningMaterialsApi.update(editId, payload)
        notifications.show({ title: 'Updated', message: 'Content updated', color: 'green' })
      } else {
        await learningMaterialsApi.create(payload)
        notifications.show({ title: 'Created', message: 'Content created', color: 'green' })
      }
      setModalOpen(false)
      fetchItems(page)
    } catch {
      notifications.show({ title: 'Error', message: 'Failed to save', color: 'red' })
    } finally {
      setSubmitting(false)
    }
  }

  function handleDelete(id: number) {
    setDeleting(true)
    learningMaterialsApi
      .delete(id)
      .then(() => {
        notifications.show({ title: 'Deleted', message: 'Content removed', color: 'green' })
        setConfirmDeleteId(null)
        fetchItems(page)
      })
      .catch(() => notifications.show({ title: 'Error', message: 'Failed to delete', color: 'red' }))
      .finally(() => setDeleting(false))
  }

  const masteryOptions = masteries.map((m) => ({
    value: String(m.id),
    label: `${m.domain_name}: ${m.name}`,
  }))

  const isDocType = mediaType === 'document'

  return (
    <Container size="lg" py="xl">
      <Stack gap="lg">
        <Group justify="space-between" align="center">
          <Box>
            <Title order={2} c="var(--sat-text)">Content Manager</Title>
            <Text c="var(--sat-text-muted)" mt={4}>{total} item{total !== 1 ? 's' : ''}</Text>
          </Box>
          <Button leftSection={<Plus size={16} />} onClick={openCreate} color="blue" radius="xl">
            Add Content
          </Button>
        </Group>

        <Group gap="sm">
          <Select
            placeholder="Media type"
            data={MEDIA_TYPES}
            value={filterType}
            onChange={(v) => { setFilterType(v); setPage(1) }}
            clearable
            radius="md"
            w={160}
          />
          <Select
            placeholder="Mastery"
            data={masteryOptions}
            value={filterMastery}
            onChange={(v) => { setFilterMastery(v); setPage(1) }}
            clearable
            searchable
            radius="md"
            w={300}
          />
        </Group>

        {loading ? (
          <Group justify="center" py="xl"><Loader /></Group>
        ) : items.length === 0 ? (
          <Text c="var(--sat-text-muted)" ta="center" py="xl">No content found.</Text>
        ) : (
          <Stack gap="md">
            <Table striped highlightOnHover withTableBorder withColumnBorders>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Title</Table.Th>
                  <Table.Th>Type</Table.Th>
                  <Table.Th>Mastery</Table.Th>
                  <Table.Th>URL</Table.Th>
                  <Table.Th>Created</Table.Th>
                  <Table.Th w={100}>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {items.map((item) => (
                  <Table.Tr key={item.id}>
                    <Table.Td fw={500}>{item.title}</Table.Td>
                    <Table.Td>
                      {item.media_type ? (
                        <Badge color={MEDIA_COLORS[item.media_type] || 'gray'} variant="light" size="sm">
                          {item.media_type}
                        </Badge>
                      ) : '—'}
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="var(--sat-text-muted)" lineClamp={1}>
                        {item.mastery_name || '—'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      {item.media_url ? (
                        <Text size="sm" c="blue" style={{ wordBreak: 'break-all' }}>{item.media_url}</Text>
                      ) : '—'}
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="var(--sat-text-muted)">
                        {item.create_datetime ? new Date(item.create_datetime).toLocaleDateString() : '—'}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap={4}>
                        <ActionIcon variant="light" color="blue" radius="xl" onClick={() => openEdit(item)}>
                          <Edit2 size={14} />
                        </ActionIcon>
                        <ActionIcon variant="light" color="red" radius="xl" onClick={() => setConfirmDeleteId(item.id)}>
                          <Trash2 size={14} />
                        </ActionIcon>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>

            {totalPages > 1 && (
              <Group justify="center">
                <Pagination total={totalPages} value={page} onChange={setPage} radius="xl" />
              </Group>
            )}
          </Stack>
        )}
      </Stack>

      {/* Create / Edit modal */}
      <Modal
        opened={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editId ? 'Edit Content' : 'Add Content'}
        radius="xl"
        size="lg"
      >
        <Stack gap="md">
          <TextInput
            label="Title"
            placeholder="e.g. SAT Reading Strategies"
            value={title}
            onChange={(e) => setTitle(e.currentTarget.value)}
            required
            radius="md"
          />
          <Group grow align="flex-start">
            <Select
              label="Media Type"
              placeholder="Select type"
              data={MEDIA_TYPES}
              value={mediaType}
              onChange={setMediaType}
              radius="md"
            />
            <Select
              label="Mastery"
              placeholder="Select mastery"
              data={masteryOptions}
              value={masteryId}
              onChange={setMasteryId}
              clearable
              searchable
              radius="md"
            />
          </Group>

          {isDocType ? (
            <Stack gap="xs">
              <FileInput
                label="Upload File"
                placeholder="Choose a file"
                value={uploadFile}
                onChange={setUploadFile}
                accept="application/pdf,.doc,.docx,.txt,.html,.xml,.json,.csv,.xls,.xlsx,.ppt,.pptx,.odt,.ods,.odp, "
                radius="md"
                clearable
              />
              {mediaUrl && !uploadFile && (
                <Text size="xs" c="dimmed">Current: {mediaUrl}</Text>
              )}
            </Stack>
          ) : (
            <TextInput
              label="Media URL"
              placeholder="https://..."
              value={mediaUrl}
              onChange={(e) => setMediaUrl(e.currentTarget.value)}
              radius="md"
            />
          )}

          <Textarea
            label="Content (Markdown & LaTeX supported)"
            placeholder="Write content here..."
            value={content}
            onChange={(e) => setContent(e.currentTarget.value)}
            minRows={6}
            autosize
            maxRows={15}
            radius="md"
          />

          <Group justify="flex-end" mt="xs">
            <Button variant="subtle" onClick={() => setModalOpen(false)} radius="xl">Cancel</Button>
            <Button onClick={handleSubmit} loading={submitting || uploading} radius="xl">
              {editId ? 'Update' : 'Create'}
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Delete confirm */}
      <Modal
        opened={confirmDeleteId !== null}
        onClose={() => setConfirmDeleteId(null)}
        title="Delete Content"
        radius="xl"
        size="sm"
      >
        <Stack gap="md">
          <Text size="sm">This will deactivate this content item. Continue?</Text>
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setConfirmDeleteId(null)} radius="xl">Cancel</Button>
            <Button
              color="red"
              loading={deleting}
              onClick={() => confirmDeleteId !== null && handleDelete(confirmDeleteId)}
              radius="xl"
            >
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  )
}
